import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';
import { MARKETPLACE_API, CACHE_TAGS } from '@/constants/residents/marketplaceConstants';
import {
  ApiResponse,
  MarketplaceNotification,
} from './types';

// ============================================================================
// Notifications API
// ============================================================================

export const notificationsApi = createApi({
  reducerPath: 'marketplaceNotificationsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [CACHE_TAGS.MarketplaceNotifications],
  endpoints: (builder) => ({
    /**
     * Get marketplace notifications (feedback received, etc.)
     */
    getMarketplaceNotifications: builder.query<MarketplaceNotification[], void>({
      query: () => ({
        url: MARKETPLACE_API.notifications.base,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<MarketplaceNotification[]> | null): MarketplaceNotification[] => {
        if (!response || !response.data) {
          console.warn('getMarketplaceNotifications: Empty or null response from API');
          return [];
        }
        return response.data;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'MarketplaceNotifications' as const, id })),
              { type: CACHE_TAGS.MarketplaceNotifications, id: 'LIST' },
            ]
          : [{ type: CACHE_TAGS.MarketplaceNotifications, id: 'LIST' }],
    }),

    /**
     * Mark a marketplace notification as read
     */
    markMarketplaceNotificationAsRead: builder.mutation<void, string>({
      query: (id) => ({
        url: MARKETPLACE_API.notifications.markAsRead(id),
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: CACHE_TAGS.MarketplaceNotifications, id },
        { type: CACHE_TAGS.MarketplaceNotifications, id: 'LIST' },
      ],
    }),

    /**
     * Delete a marketplace notification
     */
    deleteMarketplaceNotification: builder.mutation<void, string>({
      query: (id) => ({
        url: MARKETPLACE_API.notifications.delete(id),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: CACHE_TAGS.MarketplaceNotifications, id },
        { type: CACHE_TAGS.MarketplaceNotifications, id: 'LIST' },
      ],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetMarketplaceNotificationsQuery,
  useLazyGetMarketplaceNotificationsQuery,
  useMarkMarketplaceNotificationAsReadMutation,
  useDeleteMarketplaceNotificationMutation,
} = notificationsApi;

